import { appendFileSync, mkdirSync } from 'fs'
import path, { dirname } from 'path'

import { IS_DEV } from '@anvil/common/env'

import { getCliEnv } from './env'
import { getCurrentChatDir, getProjectRoot } from '../project-files'

import type { TraceWriter } from '@anvil/common/types/contracts/trace'
import type { Message } from '@anvil/common/types/messages/anvil-message'

const TRACE_FILENAME = 'trace.jsonl'

/**
 * Stringify a trace record, breaking true cycles only (shared non-cyclic
 * references are preserved). Traced messages carry tool content that can
 * smuggle a cyclic reference in; a throw here would silently kill the trace
 * line.
 */
function stringifyTraceRecord(record: Record<string, unknown>): string {
  const stack: unknown[] = []
  return JSON.stringify(record, function (this: unknown, _key, value) {
    if (stack.length > 0) {
      const thisPos = stack.indexOf(this)
      if (thisPos !== -1) {
        stack.splice(thisPos + 1)
      } else {
        stack.push(this)
      }
      if (typeof value === 'object' && value !== null && stack.includes(value)) {
        return '[Circular]'
      }
    } else {
      stack.push(value)
    }
    return value
  })
}

type AgentTraceState = {
  /** Roles of messages already written, in order. Used to detect history
   * rewrites (compaction/expiry) without holding onto message objects. */
  writtenRoles: string[]
  system: string | undefined
}

/**
 * Returns whether trace writing is enabled: always in dev, opt-in via
 * ANVIL_TRACE=1 in production builds (so users don't pay disk for a second
 * copy of their conversation by default).
 */
export function isTraceEnabled(): boolean {
  if (IS_DEV) return true
  // Same accepted values as ANVIL_FULL_TELEMETRY (see analytics-sampling)
  const flag = getCliEnv().ANVIL_TRACE
  return flag === '1' || flag === 'true' || flag === 'yes'
}

function getTraceFilePath(): string | null {
  try {
    return IS_DEV
      ? path.join(getProjectRoot(), 'debug', TRACE_FILENAME)
      : path.join(getCurrentChatDir(), TRACE_FILENAME)
  } catch {
    return null // No project root set yet
  }
}

/**
 * Create a TraceWriter that appends agent message histories to trace.jsonl —
 * one message per line, each written exactly once.
 *
 * In dev the trace goes to <projectRoot>/debug/trace.jsonl (next to
 * cli.jsonl); otherwise to the current chat directory. Returns undefined when
 * tracing is disabled.
 *
 * History rewrites (compaction, message expiry) are detected by comparing the
 * role sequence of the incoming history against what was already written; on
 * mismatch a `history_rewritten` marker line is appended followed by the new
 * history. Content-only edits that preserve the role sequence are not
 * re-traced — acceptable for a debug trace.
 */
export function createTraceWriter(
  resolveTraceFilePath: () => string | null = getTraceFilePath,
): TraceWriter | undefined {
  if (!isTraceEnabled()) {
    return undefined
  }

  const agentStates = new Map<string, AgentTraceState>()
  let ensuredDir: string | undefined

  return {
    recordStep: ({
      agentId,
      agentType,
      runId,
      userInputId,
      step,
      system,
      messages,
    }) => {
      const state = agentStates.get(agentId) ?? {
        writtenRoles: [],
        system: undefined,
      }
      const base = { agentId, agentType, runId, userInputId, step }
      const timestamp = new Date().toISOString()
      const lines: string[] = []
      const appendLine = (record: Record<string, unknown>): void => {
        lines.push(stringifyTraceRecord({ timestamp, ...record }))
      }

      const rewritten =
        messages.length < state.writtenRoles.length ||
        state.writtenRoles.some((role, i) => messages[i]?.role !== role)
      if (rewritten) {
        appendLine({
          ...base,
          type: 'history_rewritten',
          previousMessageCount: state.writtenRoles.length,
          messageCount: messages.length,
        })
        state.writtenRoles = []
      }

      if (system !== undefined && system !== state.system) {
        appendLine({ ...base, type: 'system', system })
        state.system = system
      }

      for (let i = state.writtenRoles.length; i < messages.length; i++) {
        const message = messages[i] as Message
        appendLine({
          ...base,
          type: 'message',
          index: i,
          message,
        })
      }

      state.writtenRoles = messages.map((m) => m.role)
      agentStates.set(agentId, state)

      if (lines.length === 0) return
      // Resolve the path per step (not cached for the writer's lifetime: the
      // current chat directory changes when the user starts a new chat).
      const filePath = resolveTraceFilePath()
      if (!filePath) return
      try {
        const dir = dirname(filePath)
        if (ensuredDir !== dir) {
          mkdirSync(dir, { recursive: true })
          ensuredDir = dir
        }
        appendFileSync(filePath, lines.join('\n') + '\n')
      } catch {
        // Tracing must never break the run
      }
    },
  }
}
